import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/journeyApi";
import { JourneyTopBar, JourneyFooter } from "@/components/JourneyChrome";

export default function ForgotPassword() {
  const { forgot } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email.trim()) return toast.error("Enter your email.");
    setBusy(true);
    try {
      await forgot(email.trim().toLowerCase());
      setSent(true);
    } catch (e) {
      // The endpoint never reveals whether an account exists; mirror that here.
      setSent(true);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#FAF8F4] text-[#171717]" data-testid="forgot-page">
      <JourneyTopBar showSignIn={false} />
      <main className="flex flex-1 items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          <Link to="/login" className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#2F6B57] hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
          {!sent ? (
            <div className="rounded-2xl pe-surface p-7">
              <h1 className="font-serif text-2xl text-[#171717]">Reset your password</h1>
              <p className="mt-2 text-[15px] text-[#525252]">Enter your email and we'll send a secure reset link.</p>
              <div className="relative mt-5">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A89A88]" />
                <input
                  data-testid="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full rounded-xl pe-surface py-3 pl-10 pr-3 text-[15px] outline-none transition-colors focus:border-[#2F6B57] focus:ring-2 focus:ring-[#2F6B57]/25"
                />
              </div>
              <button
                type="button"
                onClick={submit}
                disabled={busy}
                data-testid="forgot-submit"
                className="mt-4 inline-flex min-h-[52px] w-full items-center justify-center rounded-full bg-[#2F6B57] px-6 text-base font-semibold text-white transition-colors hover:bg-[#255544] disabled:opacity-60"
              >
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </div>
          ) : (
            <div className="rounded-2xl pe-surface p-7 text-center" data-testid="forgot-sent">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]">
                <CheckCircle2 className="h-7 w-7" />
              </span>
              <h1 className="mt-5 font-serif text-2xl text-[#171717]">Check your inbox</h1>
              <p className="mt-2 text-[15px] leading-relaxed text-[#525252]">
                If an account exists for <span className="font-medium">{email}</span>, a password reset link is on its way.
              </p>
            </div>
          )}
        </div>
      </main>
      <JourneyFooter />
    </div>
  );
}
